import { useMemo, useState } from 'react';
import { CalendarDays, Pencil, Plus, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { useMembers } from '@/features/members';
import { EventFormDialog } from '../components/EventFormDialog';
import { dayLabel, formatEventWhen } from '../datetime';
import {
  useCalendarEvents,
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
  useUpdateCalendarEvent,
} from '../queries';
import type { CalendarEvent, CalendarEventInput } from '../types';

function startOfTodayIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function useOwnerNames() {
  const { data } = useMembers();
  return useMemo(() => {
    const map = new Map<number, string>();
    for (const m of data?.results ?? []) map.set(m.user.id, m.user.full_name);
    return map;
  }, [data]);
}

export function CalendarPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CalendarEvent | undefined>(undefined);
  const [toDelete, setToDelete] = useState<CalendarEvent | null>(null);

  // Agenda window: everything from the start of today, ordered by start (the
  // backend orders CalendarEvent by starts_at).
  const startsAfter = useMemo(() => startOfTodayIso(), []);
  const { data, isPending, isError, error } = useCalendarEvents({ starts_after: startsAfter });
  const owners = useOwnerNames();

  const create = useCreateCalendarEvent();
  const update = useUpdateCalendarEvent();
  const remove = useDeleteCalendarEvent();

  const groups = useMemo(() => {
    const events = data?.results ?? [];
    const byDay = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const key = dayLabel(e.starts_at);
      const bucket = byDay.get(key);
      if (bucket) bucket.push(e);
      else byDay.set(key, [e]);
    }
    return [...byDay.entries()];
  }, [data]);

  function openNew() {
    setEditing(undefined);
    setDialogOpen(true);
  }

  function openEdit(event: CalendarEvent) {
    setEditing(event);
    setDialogOpen(true);
  }

  async function handleSubmit(input: CalendarEventInput) {
    if (editing) await update.mutateAsync({ id: editing.id, input });
    else await create.mutateAsync(input);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Calendar</h1>
        <Button onClick={openNew}>
          <Plus className="size-4" /> New event
        </Button>
      </div>
      <p className="text-muted-foreground text-sm">Upcoming events, from today.</p>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load events.'}
        </p>
      ) : isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : groups.length ? (
        <div className="space-y-6">
          {groups.map(([day, events]) => (
            <section key={day} className="space-y-2">
              <h2 className="text-muted-foreground text-sm font-medium">{day}</h2>
              <div className="divide-y rounded-md border">
                {events.map((event) => (
                  <div key={event.id} className="flex items-center justify-between gap-4 p-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{event.title}</span>
                        {event.kind ? <Badge variant="outline">{event.kind}</Badge> : null}
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {formatEventWhen(event.starts_at, event.ends_at, event.all_day)}
                        {event.owner ? ` · ${owners.get(event.owner) ?? '…'}` : ''}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button size="icon" variant="ghost" aria-label="Edit event" onClick={() => openEdit(event)}>
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Delete event"
                        onClick={() => setToDelete(event)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="text-muted-foreground flex flex-col items-center gap-2 rounded-md border border-dashed py-12 text-sm">
          <CalendarDays className="size-6" />
          No upcoming events. Create one to get started.
        </div>
      )}

      <EventFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        event={editing}
        onSubmit={handleSubmit}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete event?</AlertDialogTitle>
            <AlertDialogDescription>
              “{toDelete?.title}” will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
